import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useFacilityBoard } from '../contexts/FacilityBoardContext';
import {
  FacilityBoardAttachment,
  FacilityBoardMessage,
  FacilityBoardMessageDetail,
  FacilityBoardTargetMode,
  facilityBoardService,
} from '../services/facilityBoardService';
import { formatDateTimeKst } from '../utils/dateUtils';
import type { Pool } from '../types/domain';
import FileTransferProgressDialog, {
  FileTransferMode,
} from '../components/common/FileTransferProgressDialog';

export type FacilityBoardPanelProps = {
  embedded?: boolean;
  /** 중앙관제: false / 시설관리: true */
  poolScope?: boolean;
  poolId?: number | null;
  poolName?: string | null;
  pools?: Pool[];
};

const emptyForm = {
  title: '',
  body: '',
  targetMode: 'central' as FacilityBoardTargetMode,
  targetPoolIds: [] as number[],
  files: [] as File[],
};

export const FacilityBoardPanel: React.FC<FacilityBoardPanelProps> = ({
  embedded = false,
  poolScope = false,
  poolId = null,
  poolName = null,
  pools = [],
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { refreshUnread, setActivePoolId } = useFacilityBoard();
  const [items, setItems] = useState<FacilityBoardMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FacilityBoardMessage | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [existingAttachments, setExistingAttachments] = useState<FacilityBoardAttachment[]>(
    []
  );
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<FacilityBoardMessageDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferMode, setTransferMode] = useState<FileTransferMode>('upload');
  const [transferPercent, setTransferPercent] = useState<number | null>(null);
  const [transferFilePercent, setTransferFilePercent] = useState<number | null>(null);
  const [transferName, setTransferName] = useState<string | null>(null);
  const [transferFileIndex, setTransferFileIndex] = useState<number | null>(null);
  const [transferFileCount, setTransferFileCount] = useState<number | null>(null);

  const scopePoolId = poolScope ? poolId : null;
  const isCentral = !poolScope;

  useEffect(() => {
    setActivePoolId(scopePoolId ?? null);
    return () => setActivePoolId(null);
  }, [scopePoolId, setActivePoolId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await facilityBoardService.list(scopePoolId);
      setItems(res.items || []);
    } catch (e: any) {
      // 신규 시설·일시 인증 오류도 빈 목록만 (Not authenticated 붉은 스낵바 금지)
      console.warn('facility board list skipped', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [scopePoolId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      targetMode: isCentral ? 'all_pools' : 'central',
      targetPoolIds: [],
      files: [],
    });
    setExistingAttachments([]);
    setRemovedAttachmentIds([]);
    setDialogOpen(true);
  };

  const openEdit = async (row: FacilityBoardMessage, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditing(row);
    setForm({
      title: row.title,
      body: row.body,
      targetMode: 'central',
      targetPoolIds: [],
      files: [],
    });
    setExistingAttachments([]);
    setRemovedAttachmentIds([]);
    setDialogOpen(true);
    try {
      const d = await facilityBoardService.getDetail(row.id, scopePoolId);
      setExistingAttachments(d.attachments || []);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail || '첨부 목록을 불러오기 실패', {
        variant: 'error',
      });
    }
  };

  const openDetail = async (row: FacilityBoardMessage) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      if (!row.is_read) {
        try {
          await facilityBoardService.markRead(row.id, scopePoolId);
          refreshUnread(scopePoolId);
        } catch {
          /* ignore */
        }
      }
      const d = await facilityBoardService.getDetail(row.id, scopePoolId);
      setDetail(d);
      setItems((prev) =>
        prev.map((it) => (it.id === row.id ? { ...it, is_read: true } : it))
      );
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || '상세를 불러오기 실패', {
        variant: 'error',
      });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const keptAttachments = existingAttachments.filter(
    (a) => !removedAttachmentIds.includes(a.id)
  );

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    setForm((f) => {
      const room = Math.max(0, 5 - keptAttachments.length - f.files.length);
      return { ...f, files: [...f.files, ...picked].slice(0, f.files.length + room) };
    });
    e.target.value = '';
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      enqueueSnackbar('제목과 내용을 입력', { variant: 'warning' });
      return;
    }
    if (isCentral && form.targetMode === 'pools' && form.targetPoolIds.length === 0) {
      enqueueSnackbar('수신 시설을 선택', { variant: 'warning' });
      return;
    }
    if (keptAttachments.length + form.files.length > 5) {
      enqueueSnackbar('첨부는 최대 5개까지 가능', { variant: 'warning' });
      return;
    }
    const removeIds = removedAttachmentIds.slice();
    const filesSnapshot = form.files.slice(0, Math.max(0, 5 - keptAttachments.length));
    const hasFiles = filesSnapshot.length > 0;
    try {
      if (editing) {
        const messageId = editing.id;
        await facilityBoardService.update(
          messageId,
          { title: form.title, body: form.body },
          scopePoolId
        );
        for (const aid of removeIds) {
          await facilityBoardService.deleteAttachment(aid, scopePoolId);
        }
        if (hasFiles) {
          setTransferMode('upload');
          setTransferPercent(0);
          setTransferFilePercent(0);
          setTransferFileIndex(1);
          setTransferFileCount(filesSnapshot.length);
          setTransferName(filesSnapshot[0]?.name || null);
          setTransferOpen(true);
          await facilityBoardService.addAttachments(
            messageId,
            filesSnapshot,
            scopePoolId,
            {
              onUploadFileProgress: (info) => {
                setTransferPercent(info.overallPercent);
                setTransferFilePercent(info.filePercent);
                setTransferFileIndex(info.fileIndex);
                setTransferFileCount(info.fileCount);
                setTransferName(info.currentFilename || null);
              },
            }
          );
        }
        enqueueSnackbar('수정됨', { variant: 'success' });
        setExistingAttachments([]);
        setRemovedAttachmentIds([]);
      } else {
        if (hasFiles) {
          setTransferMode('upload');
          setTransferPercent(0);
          setTransferFilePercent(0);
          setTransferFileIndex(1);
          setTransferFileCount(filesSnapshot.length);
          setTransferName(filesSnapshot[0]?.name || null);
          setTransferOpen(true);
        }
        await facilityBoardService.create({
          title: form.title,
          body: form.body,
          target_mode: isCentral ? form.targetMode : 'central',
          target_pool_ids:
            isCentral && form.targetMode === 'pools' ? form.targetPoolIds : undefined,
          sender_pool_id: poolScope ? poolId : null,
          files: filesSnapshot,
          onUploadFileProgress: hasFiles
            ? (info) => {
                setTransferPercent(info.overallPercent);
                setTransferFilePercent(info.filePercent);
                setTransferFileIndex(info.fileIndex);
                setTransferFileCount(info.fileCount);
                setTransferName(info.currentFilename || null);
              }
            : undefined,
        });
        enqueueSnackbar('발송됨', { variant: 'success' });
      }
      setDialogOpen(false);
      setForm({ ...emptyForm, files: [] });
      await load();
      await refreshUnread(scopePoolId);
    } catch (e: any) {
      const detailMsg = e?.response?.data?.detail;
      const status = e?.response?.status;
      let msg = '저장에 실패';
      if (typeof detailMsg === 'string' && detailMsg.trim()) {
        msg = detailMsg;
      } else if (e?.code === 'ECONNABORTED' || /timeout/i.test(String(e?.message || ''))) {
        msg = '업로드 시간이 초과됨. 네트워크가 느리거나 파일이 매우 큼.';
      } else if (!e?.response && e?.message) {
        msg = `저장 실패: ${e.message}`;
      } else if (status === 413) {
        msg = '파일이 너무 큼. (서버/프록시 요청 크기 제한)';
      } else if (status) {
        msg = `저장에 실패. (HTTP ${status})`;
      }
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setTransferOpen(false);
      setTransferPercent(null);
      setTransferFilePercent(null);
      setTransferName(null);
      setTransferFileIndex(null);
      setTransferFileCount(null);
    }
  };

  const handleDownload = async (attachmentId: number, filename: string) => {
    setTransferMode('download');
    setTransferPercent(0);
    setTransferFilePercent(0);
    setTransferName(filename);
    setTransferFileIndex(1);
    setTransferFileCount(1);
    setTransferOpen(true);
    try {
      await facilityBoardService.downloadAttachment(
        attachmentId,
        filename,
        scopePoolId,
        (p) => {
          setTransferPercent(p);
          setTransferFilePercent(p);
        }
      );
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || '다운로드에 실패', {
        variant: 'error',
      });
    } finally {
      setTransferOpen(false);
      setTransferPercent(null);
      setTransferFilePercent(null);
      setTransferName(null);
      setTransferFileIndex(null);
      setTransferFileCount(null);
    }
  };

  const canManage = (row: FacilityBoardMessage) =>
    Boolean(row.can_edit || row.can_delete);

  const removeMessage = async (row: FacilityBoardMessage, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!window.confirm(`「${row.title}」글 삭제 확인`)) return;
    try {
      await facilityBoardService.remove(row.id, scopePoolId);
      enqueueSnackbar('삭제 완료', { variant: 'success' });
      if (detail?.id === row.id) {
        setDetailOpen(false);
        setDetail(null);
      }
      await load();
      await refreshUnread(scopePoolId);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail || '삭제에 실패', {
        variant: 'error',
      });
    }
  };

  const toggleTargetPool = (pid: number) => {
    if (!isCentral) return;
    setForm((f) => {
      const set = new Set(f.targetPoolIds);
      if (set.has(pid)) set.delete(pid);
      else set.add(pid);
      return { ...f, targetPoolIds: Array.from(set) };
    });
  };

  const content = (
    <Box>
      <FileTransferProgressDialog
        open={transferOpen}
        mode={transferMode}
        currentFilename={transferName}
        filePercent={transferFilePercent}
        overallPercent={transferPercent}
        fileIndex={transferFileIndex}
        fileCount={transferFileCount}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">시설 게시판</Typography>
          <Typography variant="body2" color="text.secondary">
            중앙관제실과 시설 간 공지·알림 (관제 메시지와 별도 운영)
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            새로고침
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            작성
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>제목</TableCell>
              <TableCell>발신</TableCell>
              <TableCell>{poolScope ? '수신처' : '수신'}</TableCell>
              <TableCell width={100}>첨부</TableCell>
              <TableCell width={160}>일시</TableCell>
              <TableCell width={80}>열람</TableCell>
              <TableCell width={96} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {loading ? '불러오는 중…' : '게시글 없음'}
                </TableCell>
              </TableRow>
            )}
            {items.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{ cursor: 'pointer', opacity: row.is_read ? 0.85 : 1 }}
                onClick={() => openDetail(row)}
              >
                <TableCell>
                  {!row.is_read && (
                    <Chip size="small" color="error" label="NEW" sx={{ mr: 1 }} />
                  )}
                  {row.title}
                </TableCell>
                <TableCell>{row.sender_label}</TableCell>
                <TableCell>
                  {poolScope && !row.is_own_sent
                    ? '-'
                    : row.recipient_labels.join(', ') || '-'}
                </TableCell>
                <TableCell>{row.attachment_count > 0 ? row.attachment_count : '-'}</TableCell>
                <TableCell>{formatDateTimeKst(row.created_at)}</TableCell>
                <TableCell>
                  {poolScope && row.is_own_sent
                    ? '-'
                    : row.is_read
                      ? '열람'
                      : '미 열람'}
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  {row.can_edit && (
                    <Tooltip title="수정">
                      <IconButton size="small" onClick={(e) => openEdit(row, e)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {(row.can_delete ?? row.can_edit) && (
                    <Tooltip title="삭제">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => removeMessage(row, e)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? '게시글 수정' : '게시글 작성'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!editing && isCentral && (
              <FormControl>
                <FormLabel>수신 대상</FormLabel>
                <RadioGroup
                  value={form.targetMode}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      targetMode: e.target.value as FacilityBoardTargetMode,
                    }))
                  }
                >
                  <FormControlLabel value="all_pools" control={<Radio />} label="전체 시설" />
                  <FormControlLabel value="pools" control={<Radio />} label="개별 시설 선택" />
                </RadioGroup>
                {form.targetMode === 'pools' && (
                  <FormGroup sx={{ pl: 2, maxHeight: 200, overflow: 'auto' }}>
                    {pools.map((p) =>
                      p.id != null ? (
                        <FormControlLabel
                          key={p.id}
                          control={
                            <Checkbox
                              checked={form.targetPoolIds.includes(Number(p.id))}
                              onChange={() => toggleTargetPool(Number(p.id))}
                            />
                          }
                          label={p.name || `시설 #${p.id}`}
                        />
                      ) : null
                    )}
                  </FormGroup>
                )}
              </FormControl>
            )}

            {!editing && poolScope && (
              <Typography variant="caption" color="text.secondary">
                발신: {poolName || `시설 #${poolId}`} · 수신: 중앙관제실
              </Typography>
            )}

            <TextField
              label="제목"
              fullWidth
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
            <TextField
              label="내용"
              fullWidth
              multiline
              minRows={4}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            />

            <Box>
              {editing && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    기존 첨부 (×로 삭제, 최대 5개)
                  </Typography>
                  {keptAttachments.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      없음
                    </Typography>
                  ) : (
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {keptAttachments.map((a) => (
                        <Chip
                          key={a.id}
                          size="small"
                          icon={<AttachFileIcon />}
                          label={a.original_name}
                          onDelete={() =>
                            setRemovedAttachmentIds((ids) =>
                              ids.includes(a.id) ? ids : [...ids, a.id]
                            )
                          }
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              )}
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFileIcon />}
                disabled={keptAttachments.length + form.files.length >= 5}
              >
                {editing ? '첨부 추가' : '첨부'} (최대 5개
                {editing
                  ? `, 남은 ${Math.max(0, 5 - keptAttachments.length - form.files.length)}`
                  : ''}
                )
                <input type="file" hidden multiple onChange={onFileChange} />
              </Button>
              {form.files.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                  {form.files.map((f, idx) => (
                    <Chip
                      key={`${f.name}-${idx}`}
                      size="small"
                      label={f.name}
                      onDelete={() =>
                        setForm((prev) => ({
                          ...prev,
                          files: prev.files.filter((_, i) => i !== idx),
                        }))
                      }
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button variant="contained" onClick={save}>
            {editing ? '수정' : '발송'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{detail?.title || '상세'}</DialogTitle>
        <DialogContent dividers>
          {detailLoading && <Typography>불러오는 중…</Typography>}
          {detail && !detailLoading && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {poolScope
                  ? `보낸 곳: ${detail.sender_label} · ${formatDateTimeKst(detail.created_at)}`
                  : `발신: ${detail.sender_label} · 수신: ${detail.recipient_labels.join(', ')} · ${formatDateTimeKst(detail.created_at)}`}
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{detail.body}</Typography>
              {detail.attachments?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2">첨부 파일</Typography>
                  {detail.attachments.map((a) => (
                    <Button
                      key={a.id}
                      size="small"
                      startIcon={<AttachFileIcon />}
                      onClick={() => handleDownload(a.id, a.original_name)}
                    >
                      {a.original_name}
                    </Button>
                  ))}
                </Box>
              )}
              {detail.recipient_status?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    수신·열람 현황
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>수신처</TableCell>
                        <TableCell>열람</TableCell>
                        <TableCell>열람 시각</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.recipient_status.map((r, idx) => (
                        <TableRow key={`${r.label}-${idx}`}>
                          <TableCell>{r.label}</TableCell>
                          <TableCell>{r.is_read ? '열람' : '미 열람'}</TableCell>
                          <TableCell>
                            {r.read_at ? formatDateTimeKst(r.read_at) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {detail && canManage(detail) && (
            <>
              {detail.can_edit && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setDetailOpen(false);
                    openEdit(detail);
                  }}
                >
                  수정
                </Button>
              )}
              {(detail.can_delete ?? detail.can_edit) && (
                <Button
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => removeMessage(detail)}
                >
                  삭제
                </Button>
              )}
            </>
          )}
          <Button onClick={() => setDetailOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <Paper sx={{ p: 2 }} elevation={embedded ? 0 : 1}>
      {content}
    </Paper>
  );
};

export default FacilityBoardPanel;
